<template>
  <v-expansion-panel :readonly="questions.length == 0">
    <v-expansion-panel-header class="py-0 secondarytext--text">
      <v-row>
        <v-col class="text-h6 my-auto">
          {{ section.title }}
          <span class="ml-2 subtitle-1">
            {{ questions.length }} questions
          </span>
        </v-col>
        <v-spacer class="limited-spacer"></v-spacer>
        <v-col cols="auto">
          <v-chip
            outlined
            color="secondary"
            class="light-background"
            @click="showEditSection"
          >
            <v-icon class="mr-2">{{ chipIcon }}</v-icon>
            {{ chipText }}
          </v-chip>
        </v-col>
        <v-col cols="auto">
          <v-menu absolute>
            <template v-slot:activator="{ on, attrs }">
              <v-btn v-bind="attrs" v-on="on" icon>
                <v-icon>more_vert</v-icon>
              </v-btn>
            </template>
            <v-list>
              <v-list-item @click="$emit('moveup')" v-if="allowUp">
                <v-list-item-icon>
                  <v-icon>arrow_upward</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Move up</v-list-item-title>
              </v-list-item>
              <v-list-item @click="$emit('movedown')" v-if="allowDown">
                <v-list-item-icon>
                  <v-icon>arrow_downward</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Move down</v-list-item-title>
              </v-list-item>
              <v-list-item @click="showEditSection">
                <v-list-item-icon>
                  <v-icon>edit</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Edit</v-list-item-title>
              </v-list-item>
              <v-list-item
                @click="$emit('edit-question', { sectionId: section.id })"
              >
                <v-list-item-icon>
                  <v-icon>add_circle</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Add Question</v-list-item-title>
              </v-list-item>
              <v-list-item @click="deleteSection(section.id)">
                <v-list-item-icon>
                  <v-icon>delete</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Delete</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </v-col>
      </v-row>
    </v-expansion-panel-header>
    <v-expansion-panel-content class="pa-0">
      <v-divider></v-divider>
      <template v-for="(item, i) in questions">
        <annual-review-question-item
          :key="i"
          :question="item"
          :allowUp="i > 0"
          :allowDown="i < questions.length - 1"
          v-on:edit="$emit('edit-question', item)"
          v-on:moveup="moveQuestionUp(i)"
          v-on:movedown="moveQuestionDown(i)"
        ></annual-review-question-item>
        <v-divider
          :key="i + 'index'"
          v-if="i < questions.length - 1"
        ></v-divider>
      </template>
    </v-expansion-panel-content>
  </v-expansion-panel>
</template>

<script>
import { mapGetters } from "vuex";
import AnnualReviewQuestionItem from "@/components/annualreviews/AnnualReviewQuestionItem";
export default {
  components: { AnnualReviewQuestionItem },
  name: "AnnualReviewSectionItem",
  props: {
    section: { type: Object },
    allowUp: { type: Boolean, default: false },
    allowDown: { type: Boolean, default: false }
  },
  data: () => ({
    saving: false,
    loading: false,
    valid: false
  }),
  computed: {
    chipIcon() {
      return this.teams.length ? "business" : "public";
    },
    chipText() {
      if (this.teams.length == 1) {
        return (
          this.teams[0].fullName ||
          (this.all_teams.find(x => x.id == this.teams[0].id) || {}).fullName
        );
      } else if (this.teams.length > 1) {
        return `${this.teams.length} Departments`;
      }
      return "Everyone";
    },
    teams() {
      return this.section.teams || [];
    },
    questions() {
      return this._.orderBy(
        this.section.questions || [],
        ["sortOrder"],
        ["asc"]
      );
    },
    ...mapGetters({
      all_teams: "team/teams"
    })
  },
  created() {},
  methods: {
    showEditSection() {
      this.$emit("edit");
    },
    moveQuestionUp(index) {
      if (index >= 0) {
        let current = this.questions[index];
        let previous = this.questions[index - 1];

        current.sortOrder = index;
        previous.sortOrder = index + 1;

        Promise.all([
          this.$store.dispatch("settings/saveAnnualReviewQuestion", current),
          this.$store.dispatch("settings/saveAnnualReviewQuestion", previous)
        ])
          .then(() => {
            this.$root.$snackbarSuccess("Order updated");
          })
          .catch(error => {
            this.logError(error);
            this.$root.$alert(
              "Error Occurred",
              "An error occurred while updating section order."
            );
          })
          .finally(() => {
            this.saving = false;
          });
      }
    },
    moveQuestionDown(index) {
      if (index >= 0) {
        let current = this.questions[index];
        let next = this.questions[index + 1];

        current.sortOrder = index + 2;
        next.sortOrder = index + 1;

        Promise.all([
          this.$store.dispatch("settings/saveAnnualReviewQuestion", current),
          this.$store.dispatch("settings/saveAnnualReviewQuestion", next)
        ])
          .then(() => {
            this.$root.$snackbarSuccess("Order updated");
          })
          .catch(error => {
            this.logError(error);
            this.$root.$alert(
              "Error Occurred",
              "An error occurred while updating section order."
            );
          })
          .finally(() => {
            this.saving = false;
          });
      }
    },
    async deleteSection(id) {
      if (
        await this.$root.$confirm(
          "Delete section",
          "Are you sure you want to delete this section?",
          { color: "red" }
        )
      ) {
        this.saving = true;
        this.$store
          .dispatch("settings/deleteAnnualReviewSection", id)
          .then(() => {
            this.$emit("save");
            this.$root.$snackbarSuccess("Section deleted");
          })
          .catch(error => {
            this.logError(error);
            this.$root.$alert(
              "Error Occurred",
              "An error occured while deleting a section."
            );
          })
          .finally(() => {
            this.saving = false;
          });
      }
    }
  }
};
</script>

<style lang="scss" scoped>
::v-deep .v-expansion-panel-content__wrap {
  padding: 0;
}
</style>
